import { URL } from 'utils/packages/rnUrlPolyfill';
const validProtocols = ['emma:', 'https:'];
function isValidProtocol(protocol) {
    return validProtocols.includes(protocol);
}
export var DeepLinkType;
(function (DeepLinkType) {
    DeepLinkType["NONE"] = "NONE";
    DeepLinkType["LOGIN_SUCCESS"] = "LOGIN_SUCCESS";
    DeepLinkType["LOGIN_FAIL"] = "LOGIN_FAIL";
    DeepLinkType["LOGIN_ABORT"] = "LOGIN_ABORT";
    DeepLinkType["EMAIL_VERIFY"] = "EMAIL_VERIFY";
    DeepLinkType["PAYMENT_SUCCESS"] = "PAYMENT_SUCCESS";
    DeepLinkType["PAYMENT_FAIL"] = "PAYMENT_FAIL";
    DeepLinkType["PAYMENT_ABORT"] = "PAYMENT_ABORT";
    DeepLinkType["VRP_CONSENT_SUCCESS"] = "VRP_CONSENT_SUCCESS";
    DeepLinkType["VRP_CONSENT_ABORT"] = "VRP_CONSENT_ABORT";
    DeepLinkType["VRP_CONSENT_FAIL"] = "VRP_CONSENT_FAIL";
    DeepLinkType["APP_LINK"] = "APP_LINK";
    DeepLinkType["WEB_SIGN_IN"] = "WEB_SIGN_IN";
    DeepLinkType["CHOOSE_BANK"] = "CHOOSE_BANK";
    DeepLinkType["REFERRAL_INVITE"] = "REFERRAL_INVITE";
})(DeepLinkType || (DeepLinkType = {}));
const hasProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//;
export const ONELINK_DOMAINS = [
    'onelink.emma-app.com',
    'emma.onelink.me',
    'awin.onelink.me',
    'emmapublisher.onelink.me',
];
/**
 * AppsFlyer OneLink `deep_link_value`s are usually configured as bare paths
 * (`nordvpn`, `/invite-code/ABC123`) rather than full urls, so they need the app
 * scheme adding before `parseDeepLink` can make sense of them. Values that
 * already carry a protocol are passed through untouched.
 *
 * @param deepLinkValue string
 * @returns a deeplink string ready for parseDeepLink
 */
export const normalizeOneLinkDeepLinkValue = (deepLinkValue) => {
    const value = deepLinkValue.trim();
    if (!value || hasProtocol.test(value)) {
        return value;
    }
    return `emma://${value.replace(/^\/+/, '')}`;
};
/**
 * Reads the `deep_link_value` of a long OneLink url without going through the
 * AppsFlyer SDK. Short links carry no query params and return undefined.
 *
 * @param url string
 * @returns a deeplink string ready for parseDeepLink, or undefined
 */
export const getOneLinkDeepLinkValue = (url) => {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return undefined;
    }
    if (!ONELINK_DOMAINS.includes(parsed.hostname)) {
        return undefined;
    }
    const deepLinkValue = parsed.searchParams.get('deep_link_value');
    return deepLinkValue
        ? normalizeOneLinkDeepLinkValue(deepLinkValue) || undefined
        : undefined;
};
/**
 * Whether the url is a OneLink short link (`https://onelink.emma-app.com/{template}/{id}`),
 * whose `deep_link_value` can only be read by asking AppsFlyer.
 *
 * @param url string
 */
export const isOneLinkShortLink = (url) => {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return false;
    }
    return (ONELINK_DOMAINS.includes(parsed.hostname) &&
        parsed.pathname.split('/').filter(Boolean).length === 2);
};
/**
 * Parses emma:// and https://emma-app.com/ deeplinks
 * If this function grows too large, extract feature-specific handlers into modules
 *
 * @param deepLink string
 * @returns DeepLinkData
 */
export const parseDeepLink = (deepLink) => {
    const [protocol, rest] = deepLink.split('//');
    if (!isValidProtocol(protocol)) {
        // log error
        return {
            type: DeepLinkType.NONE,
        };
    }
    let url;
    try {
        url = new URL(
        // URL() parses urls with custom (like emma://) and regular (like https://) protocols differently.
        // This converts emma:// links to https:// ones for uniform parsing
        protocol !== 'https:' ? `https://emma-app.com/${rest ?? ''}` : deepLink);
    }
    catch {
        // log error
        return {
            type: DeepLinkType.NONE,
        };
    }
    const { pathname, searchParams } = url;
    if (pathname === '/dashboard/email/verify') {
        const token = searchParams.get('token');
        if (token) {
            return {
                type: DeepLinkType.EMAIL_VERIFY,
                token,
            };
        }
        return {
            type: DeepLinkType.NONE,
        };
    }
    // Login callbacks
    if (pathname === '/loginsuccess' && protocol === 'emma:') {
        return {
            type: DeepLinkType.LOGIN_SUCCESS,
        };
    }
    if (pathname === '/loginfail' && protocol === 'emma:') {
        const error = searchParams.get('error');
        const userMessage = searchParams.get('userMessage');
        const ref = searchParams.get('ref');
        return {
            type: DeepLinkType.LOGIN_FAIL,
            error,
            userMessage,
            ref,
        };
    }
    if (pathname === '/choose-bank' && protocol === 'emma:') {
        const accountType = searchParams.get('type');
        if (accountType) {
            return {
                type: DeepLinkType.CHOOSE_BANK,
                accountType: accountType,
            };
        }
    }
    if (pathname === '/loginabort' && protocol === 'emma:') {
        return {
            type: DeepLinkType.LOGIN_ABORT,
        };
    }
    if (protocol === 'emma:') {
        if (pathname.startsWith('/invite-code')) {
            const code = pathname.split('/').pop();
            return {
                code,
                type: DeepLinkType.REFERRAL_INVITE,
            };
        }
        switch (pathname) {
            // Payment callbacks
            case '/paymentsuccess':
                return {
                    type: DeepLinkType.PAYMENT_SUCCESS,
                };
            case '/paymentfail':
                return {
                    type: DeepLinkType.PAYMENT_FAIL,
                };
            case '/paymentabort':
                return {
                    type: DeepLinkType.PAYMENT_ABORT,
                };
            case '/vrp-consent-success':
                return {
                    type: DeepLinkType.VRP_CONSENT_SUCCESS,
                };
            case '/vrp-consent-fail':
                return {
                    type: DeepLinkType.VRP_CONSENT_FAIL,
                };
            case '/vrp-consent-abort':
                return {
                    type: DeepLinkType.VRP_CONSENT_ABORT,
                };
            case '/web-sign-in': {
                const clientId = searchParams.get('clientId');
                if (clientId && clientId !== 'undefined' && clientId !== 'null') {
                    return {
                        type: DeepLinkType.WEB_SIGN_IN,
                        clientId,
                    };
                }
                break;
            }
            default:
                break;
        }
    }
    return {
        routeStr: url.pathname + url.search,
        type: DeepLinkType.APP_LINK,
    };
};
